import { useCallback, useEffect } from 'react';

import { useAuthStore } from '../stores/authStore';
import { useOrgUiSettingsStore } from '../stores/orgUiSettingsStore';
import organizationService from '../services/organizationService';
import { DEFAULT_ORG_MODULES, ORG_MODULES, type OrgModule } from '../types';

export function isOrgModule(value: string): value is OrgModule {
  return (ORG_MODULES as readonly string[]).includes(value);
}

/**
 * Loads the active organization's experience & module settings into the store.
 * Mount once in the authenticated layout (AppLayout).
 */
export function useLoadOrgUiSettings(): void {
  const orgId = useAuthStore((s) => s.user?.organization_id ?? null);
  const setSettings = useOrgUiSettingsStore((s) => s.setSettings);

  useEffect(() => {
    let cancelled = false;
    if (!orgId) {
      setSettings(null);
      return;
    }
    organizationService
      .getUiSettings(orgId)
      .then((s) => {
        if (!cancelled) setSettings(s);
      })
      .catch(() => {
        // Non-critical: defaults keep today's behaviour (Olisé modules off).
        if (!cancelled) setSettings(null);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, setSettings]);
}

/**
 * Module gate. Until settings load (or without an org) the defaults apply, so
 * existing features stay visible and the new Olisé ones stay hidden.
 */
export function useOrgModules() {
  const settings = useOrgUiSettingsStore((s) => s.settings);
  // False until the first load settles: decisions that deny access must wait for it.
  const loaded = useOrgUiSettingsStore((s) => s.loaded);
  const moduleEnabled = useCallback(
    (module: OrgModule): boolean => settings?.modules?.[module] ?? DEFAULT_ORG_MODULES[module],
    [settings]
  );
  return { moduleEnabled, experience: settings?.experience ?? 'etabli', settings, loaded };
}
